import React, { useEffect, useState } from 'react';
import { DollarSign, Users, Clock, AlertCircle, Download, Mail, Eye, Archive } from 'lucide-react';
import { useEmployees } from '../hooks/useEmployees';
import { usePayroll } from '../hooks/usePayroll';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import PasswordChange from './PasswordChange';
import PayStubModal from './PayStubModal';
import { generatePayStubPDF } from '../utils/pdfGenerator';
import { sendPayStubEmail } from '../utils/emailService';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { generatePayStubsZip } from '../utils/zipGenerator';
import { parseLocalDateForDisplay } from '../utils/payrollPeriodCalculator';
import type { PayrollEntry, Employee } from '../types';

const Dashboard = () => {
  const { employees, loading: employeesLoading } = useEmployees();
  const { payrollEntries, loading: payrollLoading } = usePayroll();
  const { isAdmin, session } = useAuth();
  const [showIssues, setShowIssues] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<PayrollEntry | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | undefined>();
  const { settings: companySettings } = useCompanySettings();
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setCurrentUserEmail(session?.user?.email);
    });
  }, []);

  useEffect(() => {
    if (currentUserEmail && !employeesLoading) {
      const employee = employees.find(e => e.email === currentUserEmail);
      setCurrentEmployee(employee || null);
    }
  }, [currentUserEmail, employees, employeesLoading]);

  useEffect(() => {
    console.log('=== Dashboard Debug Info ===');
    console.log('Current user email:', currentUserEmail);
    console.log('Is admin:', isAdmin);
    console.log('Employees count:', employees.length);
    console.log('Payroll entries count:', payrollEntries.length);
    console.log('Session:', session?.user?.email);
    if (employees.length > 0) {
      console.log('Sample employee:', employees[0]);
    }
    if (payrollEntries.length > 0) {
      console.log('Sample payroll entry:', payrollEntries[0]);
    }
  }, [currentUserEmail, isAdmin, employees, payrollEntries, session]);

  const currentUserPayroll = payrollEntries.filter(entry => {
    const employee = employees.find(e => e.id === entry.employeeId);
    return employee?.email === currentUserEmail;
  });

  const handleEmailPaystub = async (entry: PayrollEntry) => {
    const employee = employees.find(e => e.id === entry.employeeId);
    if (!employee) return;

    try {
      await sendPayStubEmail(entry, employee);
      alert('Paystub sent successfully!');
    } catch (error) {
      console.error('Error sending paystub:', error);
      alert('Failed to send paystub. Please try again.');
    }
  };

  const handleDownloadPDF = async (entry: PayrollEntry) => {
    const employee = employees.find(e => e.id === entry.employeeId);
    if (!employee) return;

    try {
      await generatePayStubPDF(entry, employee, false, companySettings);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDownloadAllPaystubs = async () => {
    if (!currentEmployee) {
      alert('Unable to find employee information. Please try again later.');
      return;
    }

    try {
      const successCount = await generatePayStubsZip(currentUserPayroll, currentEmployee, companySettings);
      if (successCount > 0) {
        // Success message is optional since the download will start automatically
        console.log(`Successfully generated ${successCount} paystubs`);
      }
    } catch (error) {
      console.error('Error downloading paystubs:', error);
      alert(error instanceof Error ? error.message : 'Failed to download paystubs. Please try again.');
    }
  };

  const currentYear = new Date().getFullYear();
  const currentYearEntries = payrollEntries.filter(entry => {
    const payDate = entry.payDate
      ? parseLocalDateForDisplay(entry.payDate)
      : parseLocalDateForDisplay(entry.payPeriod.end);
    return payDate.getFullYear() === currentYear;
  });

  const totalPayroll = currentYearEntries.reduce((sum, entry) => sum + entry.grossPay, 0);
  const pendingApprovals = payrollEntries.filter(entry => entry.status === 'pending').length;
  const activeEmployees = employees.length;
  const issues = payrollEntries.filter(entry =>
    entry.status === 'pending' &&
    parseLocalDateForDisplay(entry.payPeriod.end) < new Date()
  ).length;

  const issuesList = payrollEntries.filter(entry => 
    entry.status === 'pending' && 
    parseLocalDateForDisplay(entry.payPeriod.end) < new Date()
  ).map(entry => {
    const employee = employees.find(e => e.id === entry.employeeId);
    return {
      ...entry,
      employeeName: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown Employee'
    };
  });

  if (employeesLoading || payrollLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
      
      {isAdmin ? (
        <>
          {/* Admin Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Payroll ({currentYear})</p>
                  <p className="text-xl font-semibold text-gray-900">
                    ${totalPayroll.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active Employees</p>
                  <p className="text-xl font-semibold text-gray-900">{activeEmployees}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending Approvals</p>
                  <p className="text-xl font-semibold text-gray-900">{pendingApprovals}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">Issues</p>
                    {issues > 0 && (
                      <button
                        onClick={() => setShowIssues(!showIssues)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        {showIssues ? 'Hide Details' : 'Show Details'}
                      </button>
                    )}
                  </div>
                  <p className="text-xl font-semibold text-gray-900">
                    {issues} {issues === 1 ? 'Issue' : 'Issues'}
                  </p>
                </div>
              </div>
              {showIssues && issues > 0 && (
                <div className="mt-2 space-y-2">
                  {issuesList.map((issue) => (
                    <div key={issue.id} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      <p>
                        <span className="font-medium">{issue.employeeName}</span> has pending payroll for period{' '}
                        {parseLocalDateForDisplay(issue.payPeriod.start).toLocaleDateString()} - {new Date(issue.payPeriod.end).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {payrollEntries.slice(0, 5).map((entry) => {
                  const employee = employees.find(e => e.id === entry.employeeId);
                  return (
                    <div key={entry.id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Payroll processed for {employee?.firstName} {employee?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">
                          ${entry.grossPay.toLocaleString()} - {parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-sm rounded-full ${
                        entry.status === 'paid' 
                          ? 'text-green-700 bg-green-100'
                          : entry.status === 'pending'
                          ? 'text-yellow-700 bg-yellow-100'
                          : 'text-blue-700 bg-blue-100'
                      }`}>
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Employee View */
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">My Payroll History</h2>
              {currentUserPayroll.length > 0 && (
                <button
                  onClick={handleDownloadAllPaystubs}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                  title="Download All Paystubs"
                >
                  <Archive className="h-4 w-4" />
                  Download All Paystubs
                </button>
              )}
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {currentUserPayroll.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Pay Period: {parseLocalDateForDisplay(entry.payPeriod.start).toLocaleDateString()} - {parseLocalDateForDisplay(entry.payPeriod.end).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-500">
                        Gross Pay: ${entry.grossPay.toLocaleString()}
                        <br />
                        Net Pay: ${entry.netPay.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 text-sm rounded-full ${
                        entry.status === 'paid' 
                          ? 'text-green-700 bg-green-100'
                          : entry.status === 'pending'
                          ? 'text-yellow-700 bg-yellow-100'
                          : 'text-blue-700 bg-blue-100'
                      }`}>
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Paystub"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(entry)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Download PDF"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEmailPaystub(entry)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Email Paystub"
                        >
                          <Mail className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {currentUserPayroll.length === 0 && (
                  <p className="text-sm text-gray-500">No payroll entries found.</p>
                )}
              </div>
              {showModal && selectedEntry && (
                <PayStubModal
                  entry={selectedEntry}
                  employee={employees.find(e => e.id === selectedEntry.employeeId)!}
                  onClose={() => {
                    setShowModal(false);
                    setSelectedEntry(null);
                  }}
                  onDownload={() => handleDownloadPDF(selectedEntry)}
                  onEmail={() => handleEmailPaystub(selectedEntry)}
                />
              )}
            </div>
          </div>

          {/* Password Change Section */}
          <PasswordChange />
        </div>
      )}
    </div>
  );
};

export default Dashboard;